import React from 'react';
import { Box, Chip, Button, Typography, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Wifi, WifiOff, Refresh, Error } from '@mui/icons-material';

interface ConnectionStatusProps {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  onReconnect?: () => void;
  sx?: SxProps<Theme>;
}

export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  isConnected,
  connectionState,
  onReconnect,
  sx = {}
}) => {
  const getStatusConfig = () => {
    switch (connectionState) {
      case 'connected':
        return {
          icon: <Wifi />,
          text: 'Connected',
          color: 'success' as const,
          variant: 'filled' as const
        };
      case 'connecting':
        return {
          icon: <Refresh sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />,
          text: 'Connecting...',
          color: 'info' as const,
          variant: 'filled' as const
        };
      case 'error':
        return {
          icon: <Error />,
          text: 'Connection Error',
          color: 'error' as const,
          variant: 'filled' as const
        };
      case 'disconnected':
      default:
        return {
          icon: <WifiOff />,
          text: 'Disconnected',
          color: 'default' as const,
          variant: 'outlined' as const
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ...sx }}>
      <Chip
        icon={config.icon}
        label={config.text}
        color={config.color}
        variant={config.variant}
        size="small"
      />
      
      {(connectionState === 'error' || connectionState === 'disconnected') && onReconnect && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={onReconnect}
          color="primary"
        >
          Retry
        </Button>
      )}
      
      {connectionState === 'disconnected' && (
        <Typography variant="caption" color="text.secondary">
          Real-time updates disabled
        </Typography>
      )}
    </Box>
  );
};